import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Cable as CableIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import axios from '../api/axios';

const EMPTY_FORM = {
  source_device_id: '',
  target_device_id: '',
  connection_type_id: '',
  source_interface_id: '',
  target_interface_id: '',
  subnet_id: '',
  vlan: '',
  protocol: '',
  comment: '',
  cable_length: '',
  cable_route: '',
  cable_label: '',
};

function Connections() {
  const [connections, setConnections] = useState([]);
  const [devices, setDevices] = useState([]);
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [subnets, setSubnets] = useState([]);
  const [sourceInterfaces, setSourceInterfaces] = useState([]);
  const [targetInterfaces, setTargetInterfaces] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [connsRes, devsRes, typesRes, subsRes] = await Promise.all([
        axios.get('/connections/'),
        axios.get('/devices/'),
        axios.get('/connection-types/'),
        axios.get('/subnets/'),
      ]);
      setConnections(connsRes.data);
      setDevices(devsRes.data.items || devsRes.data);
      setConnectionTypes(typesRes.data);
      setSubnets(subsRes.data.items || subsRes.data || []);
    } catch (err) {
      setError('Ошибка загрузки данных: ' + (err.response?.data?.detail || err.message));
    }
  };

  const fetchInterfaces = async (deviceId) => {
    if (!deviceId) return [];
    try {
      const r = await axios.get(`/devices/${deviceId}/interfaces`);
      return r.data || [];
    } catch {
      return [];
    }
  };

  const loadPhotos = async (connId) => {
    try {
      const r = await axios.get(`/connections/${connId}/photos`);
      setPhotos(r.data || []);
    } catch {
      setPhotos([]);
    }
  };

  const handleSourceDevice = async (val) => {
    setFormData((f) => ({ ...f, source_device_id: val, source_interface_id: '' }));
    setSourceInterfaces(await fetchInterfaces(val));
  };
  const handleTargetDevice = async (val) => {
    setFormData((f) => ({ ...f, target_device_id: val, target_interface_id: '' }));
    setTargetInterfaces(await fetchInterfaces(val));
  };

  const handleOpenDialog = async (item = null) => {
    setError('');
    setPhotos([]);
    if (item) {
      setEditingItem(item);
      setFormData({
        source_device_id: item.source_device_id,
        target_device_id: item.target_device_id,
        connection_type_id: item.connection_type_id,
        source_interface_id: item.source_interface_id || '',
        target_interface_id: item.target_interface_id || '',
        subnet_id: item.subnet_id || '',
        vlan: item.vlan || '',
        protocol: item.protocol || '',
        comment: item.comment || '',
        cable_length: item.cable_length ?? '',
        cable_route: item.cable_route || '',
        cable_label: item.cable_label || '',
      });
      setSourceInterfaces(await fetchInterfaces(item.source_device_id));
      setTargetInterfaces(await fetchInterfaces(item.target_device_id));
      loadPhotos(item.id);
    } else {
      setEditingItem(null);
      setFormData({ ...EMPTY_FORM });
      setSourceInterfaces([]);
      setTargetInterfaces([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({ ...EMPTY_FORM });
    setSourceInterfaces([]);
    setTargetInterfaces([]);
    setPhotos([]);
    setError('');
  };

  const buildPayload = () => {
    const toInt = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const toFloat = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
    const str = (v) => (v === '' ? null : v);
    return {
      source_device_id: toInt(formData.source_device_id),
      target_device_id: toInt(formData.target_device_id),
      connection_type_id: toInt(formData.connection_type_id),
      source_interface_id: toInt(formData.source_interface_id),
      target_interface_id: toInt(formData.target_interface_id),
      subnet_id: toInt(formData.subnet_id),
      vlan: str(formData.vlan),
      protocol: str(formData.protocol),
      comment: str(formData.comment),
      cable_length: toFloat(formData.cable_length),
      cable_route: str(formData.cable_route),
      cable_label: str(formData.cable_label),
    };
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      const payload = buildPayload();
      if (editingItem) {
        await axios.put(`/connections/${editingItem.id}`, payload);
      } else {
        await axios.post('/connections/', payload);
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editingItem) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploadingPhoto(true);
      await axios.post(`/connections/${editingItem.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadPhotos(editingItem.id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!editingItem) return;
    try {
      await axios.delete(`/connections/${editingItem.id}/photos/${photoId}`);
      await loadPhotos(editingItem.id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить это подключение?')) return;
    try {
      await axios.delete(`/connections/${id}`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const getDeviceName = (deviceId) => {
    const device = devices.find((d) => d.id === deviceId);
    return device ? device.name : `ID: ${deviceId}`;
  };
  const getConnectionTypeName = (typeId) => {
    const type = connectionTypes.find((t) => t.id === typeId);
    return type ? type.name : `ID: ${typeId}`;
  };
  const getConnectionTypeColor = (typeId) => {
    const type = connectionTypes.find((t) => t.id === typeId);
    return type?.color || '#666666';
  };
  const ifaceLabel = (i) => `${i.name}${i.ip_address ? ` (${i.ip_address})` : ''}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Подключения между устройствами</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={devices.length < 2 || connectionTypes.length === 0}
        >
          Добавить подключение
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {devices.length < 2 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Необходимо как минимум 2 устройства для создания подключения
        </Alert>
      )}
      {connectionTypes.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Сначала создайте типы подключений
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Тип</TableCell>
              <TableCell>Устройство 1</TableCell>
              <TableCell align="center">→</TableCell>
              <TableCell>Устройство 2</TableCell>
              <TableCell>Подсеть / VLAN</TableCell>
              <TableCell>Кабель</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Chip
                    icon={<CableIcon />}
                    label={item.connection_type_name || getConnectionTypeName(item.connection_type_id)}
                    size="small"
                    sx={{
                      bgcolor: getConnectionTypeColor(item.connection_type_id) + '20',
                      borderColor: getConnectionTypeColor(item.connection_type_id),
                      borderWidth: 2,
                      borderStyle: 'solid',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {item.source_device_name || getDeviceName(item.source_device_id)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <CableIcon sx={{ color: getConnectionTypeColor(item.connection_type_id) }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {item.target_device_name || getDeviceName(item.target_device_id)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {(subnets.find((s) => s.id === item.subnet_id)?.name) || '—'}
                    {item.vlan ? ` · VLAN ${item.vlan}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {item.cable_length ? `${item.cable_length} м` : '—'}
                    {item.cable_label ? ` · ${item.cable_label}` : ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => handleOpenDialog(item)} title="Редактировать">
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(item.id)} title="Удалить">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {connections.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Нет подключений. Создайте первое подключение!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания/редактирования */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Редактировать подключение' : 'Добавить подключение'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Устройство 1 (источник)</InputLabel>
            <Select
              value={formData.source_device_id}
              label="Устройство 1 (источник)"
              onChange={(e) => handleSourceDevice(e.target.value)}
              required
            >
              {devices.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.name} ({device.device_type_name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Устройство 2 (назначение)</InputLabel>
            <Select
              value={formData.target_device_id}
              label="Устройство 2 (назначение)"
              onChange={(e) => handleTargetDevice(e.target.value)}
              required
            >
              {devices
                .filter((d) => d.id !== formData.source_device_id)
                .map((device) => (
                  <MenuItem key={device.id} value={device.id}>
                    {device.name} ({device.device_type_name})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Тип подключения</InputLabel>
            <Select
              value={formData.connection_type_id}
              label="Тип подключения"
              onChange={(e) => setFormData({ ...formData, connection_type_id: e.target.value })}
              required
            >
              {connectionTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: type.color }} />
                    {type.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">Сеть (необязательно)</Typography>
          </Divider>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Интерфейс источника</InputLabel>
              <Select
                value={formData.source_interface_id}
                label="Интерфейс источника"
                onChange={(e) => setFormData({ ...formData, source_interface_id: e.target.value })}
                disabled={!formData.source_device_id}
              >
                <MenuItem value=""><em>— не указан —</em></MenuItem>
                {sourceInterfaces.map((i) => (
                  <MenuItem key={i.id} value={i.id}>{ifaceLabel(i)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Интерфейс приёмника</InputLabel>
              <Select
                value={formData.target_interface_id}
                label="Интерфейс приёмника"
                onChange={(e) => setFormData({ ...formData, target_interface_id: e.target.value })}
                disabled={!formData.target_device_id}
              >
                <MenuItem value=""><em>— не указан —</em></MenuItem>
                {targetInterfaces.map((i) => (
                  <MenuItem key={i.id} value={i.id}>{ifaceLabel(i)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Подсеть</InputLabel>
              <Select
                value={formData.subnet_id}
                label="Подсеть"
                onChange={(e) => setFormData({ ...formData, subnet_id: e.target.value })}
              >
                <MenuItem value=""><em>— не указана —</em></MenuItem>
                {subnets.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}{s.vlan ? ` · VLAN ${s.vlan}` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField fullWidth label="VLAN" value={formData.vlan}
              onChange={(e) => setFormData({ ...formData, vlan: e.target.value })} />
            <TextField fullWidth label="Протокол" value={formData.protocol}
              onChange={(e) => setFormData({ ...formData, protocol: e.target.value })} />
          </Stack>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">Кабель (необязательно)</Typography>
          </Divider>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth type="number" label="Длина, м" value={formData.cable_length}
              onChange={(e) => setFormData({ ...formData, cable_length: e.target.value })}
              inputProps={{ step: '0.1', min: 0 }} />
            <TextField fullWidth label="Бирка / маркировка" value={formData.cable_label}
              onChange={(e) => setFormData({ ...formData, cable_label: e.target.value })} />
          </Stack>
          <TextField fullWidth label="Где проложен (трасса)" value={formData.cable_route}
            onChange={(e) => setFormData({ ...formData, cable_route: e.target.value })}
            margin="normal" multiline rows={2} />

          <TextField fullWidth label="Комментарий" value={formData.comment}
            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
            margin="normal" multiline rows={2} />

          {editingItem && (
            <>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">Фото</Typography>
              </Divider>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {photos.map((p) => (
                  <Box key={p.id} sx={{ position: 'relative' }}>
                    <a href={p.url} target="_blank" rel="noopener noreferrer">
                      <img src={p.thumbnail_url || p.url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                    </a>
                    <IconButton size="small" onClick={() => handlePhotoDelete(p.id)}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'error.light' } }}>
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                ))}
                <Button component="label" variant="outlined" startIcon={<PhotoCameraIcon />} disabled={uploadingPhoto} sx={{ height: 72 }}>
                  {uploadingPhoto ? 'Загрузка…' : 'Фото'}
                  <input type="file" hidden accept="image/jpeg,image/png,image/gif,image/webp" onChange={handlePhotoUpload} />
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.source_device_id || !formData.target_device_id || !formData.connection_type_id}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Connections;
